import React, { createContext, useContext, useState, useCallback } from 'react';
import api, { session } from '../ui/api';

const AuthCtx = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

/**
 * Passwordless session state: name (+ who you are) -> SMS or access
 * code -> one 30-day token. No password, no refresh cycle; an expired
 * or revoked token is caught by the API client on the next call.
 *
 * `user.person_id` is the pupil the login is linked to. A pupil sees only
 * that record; a father/mother sees every child of the family, which
 * StudentDataContext loads (GET /students) — this id is just the default.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => session.user());

  const lookup = useCallback((name, parentType) => api.loginLookup(name, parentType), []);
  const select = useCallback((userId) => api.loginSelect(userId), []);

  const verify = useCallback(async (userId, code) => {
    const data = await api.loginVerify(userId, code, navigator.userAgent);
    session.save(data.token, data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* the local session goes either way */ }
    session.clear();
    setUser(null);
  }, []);

  const value = {
    user,
    studentId: user?.person_id ?? null,
    parentType: user?.parent_type ?? 'none',
    lookup,
    select,
    verify,
    logout,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
